import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Plus, Pencil, Trash2, Eye, EyeOff, X, MailOpen } from 'lucide-react';
import SEO from '../../components/SEO';
import api from '../../lib/api';
import { INSIGHT_CATEGORIES, CASE_CATEGORIES } from '../../data/content';

const emptyInsight = { title: '', category: INSIGHT_CATEGORIES[0], summary: '', content: '', cover_image: '', author: '', published: false };
const emptyCase = { title: '', category: CASE_CATEGORIES[0], sector: '', geography: '', client_type: '', background: '', challenge: '', objectives: '', scope_of_work: '', methodology: '', deliverables: '', outcomes: '', cover_image: '', published: false };

const inputCls = 'w-full rounded-lg border border-brand-grey bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-brand-green';
const labelCls = 'mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-brand-body';

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get('/admin/enquiries').then((r) => setItems(r.data)).catch(() => toast.error('Failed to load enquiries'));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.patch(`/admin/enquiries/${id}/read`); load(); };
  const remove = async (id) => { await api.delete(`/admin/enquiries/${id}`); toast.success('Enquiry deleted'); setOpen(null); load(); };

  return (
    <div data-testid="admin-enquiries-tab">
      {items.length === 0 && <p className="py-16 text-center text-sm text-brand-body">No enquiries yet. New contact form submissions will appear here.</p>}
      <div className="space-y-3">
        {items.map((e) => (
          <div key={e.enquiry_id} className={`rounded-xl border bg-white p-5 ${e.read ? 'border-brand-grey' : 'border-brand-green/40'}`} data-testid={`enquiry-${e.enquiry_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-heading text-sm font-bold text-brand-black">
                  {!e.read && <span className="mr-2 inline-block h-2 w-2 rounded-full bg-brand-green align-middle" />}
                  {e.full_name} <span className="font-normal text-brand-body">· {e.organisation}</span>
                </p>
                <p className="mt-1 text-xs text-brand-body">{e.service} · {new Date(e.created_at).toLocaleString('en-IN')}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setOpen(open === e.enquiry_id ? null : e.enquiry_id); if (!e.read) markRead(e.enquiry_id); }} data-testid={`enquiry-view-${e.enquiry_id}`} className="rounded-full border border-brand-grey px-4 py-2 text-xs font-semibold hover:border-brand-green">
                  {open === e.enquiry_id ? 'Close' : 'View'}
                </button>
                <button onClick={() => remove(e.enquiry_id)} data-testid={`enquiry-delete-${e.enquiry_id}`} className="rounded-full border border-brand-grey p-2 text-brand-body hover:border-red-300 hover:text-red-500" aria-label="Delete enquiry">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {open === e.enquiry_id && (
              <div className="mt-4 space-y-2 border-t border-brand-grey pt-4 text-sm text-brand-body">
                <p><span className="font-semibold text-brand-black">Email:</span> <a href={`mailto:${e.email}`} className="text-brand-deepgreen">{e.email}</a></p>
                {e.phone && <p><span className="font-semibold text-brand-black">Phone:</span> {e.phone}</p>}
                {e.location && <p><span className="font-semibold text-brand-black">Location:</span> {e.location}</p>}
                <p className="whitespace-pre-wrap pt-2 leading-relaxed">{e.description}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentTab({ kind }) {
  const isInsight = kind === 'insights';
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const endpoint = isInsight ? '/admin/insights' : '/admin/case-studies';
  const idKey = isInsight ? 'insight_id' : 'case_id';
  const empty = isInsight ? emptyInsight : emptyCase;
  const categories = isInsight ? INSIGHT_CATEGORIES : CASE_CATEGORIES;

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [kind]);

  const save = async () => {
    try {
      if (editing[idKey]) {
        await api.put(`${endpoint}/${editing[idKey]}`, editing);
        toast.success('Updated');
      } else {
        await api.post(endpoint, editing);
        toast.success('Created');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item permanently?')) return;
    await api.delete(`${endpoint}/${id}`);
    toast.success('Deleted');
    load();
  };

  const togglePublish = async (item) => {
    await api.put(`${endpoint}/${item[idKey]}`, { ...item, published: !item.published });
    load();
  };

  const field = (key, label, textarea = false, rows = 3) => (
    <div key={key}>
      <label className={labelCls}>{label}</label>
      {textarea
        ? <textarea rows={rows} className={inputCls} value={editing[key] || ''} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} data-testid={`editor-${key}`} />
        : <input className={inputCls} value={editing[key] || ''} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} data-testid={`editor-${key}`} />}
    </div>
  );

  if (editing) {
    return (
      <div className="rounded-2xl border border-brand-grey bg-white p-6 sm:p-8" data-testid={`${kind}-editor`}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-brand-black">{editing[idKey] ? 'Edit' : 'New'} {isInsight ? 'Article' : 'Case Study'}</h3>
          <button onClick={() => setEditing(null)} className="rounded-full p-2 hover:bg-brand-offwhite" aria-label="Close editor" data-testid="editor-close"><X size={18} /></button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {field('title', 'Title *')}
          <div>
            <label className={labelCls}>Category *</label>
            <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="editor-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {isInsight ? (
            <>
              {field('author', 'Author')}
              {field('cover_image', 'Cover image URL')}
              <div className="sm:col-span-2">{field('summary', 'Summary', true, 2)}</div>
              <div className="sm:col-span-2">{field('content', 'Full article content', true, 12)}</div>
            </>
          ) : (
            <>
              {field('sector', 'Sector')}
              {field('geography', 'Geography')}
              {field('client_type', 'Client type (e.g. Confidential)')}
              {field('cover_image', 'Cover image URL')}
              <div className="sm:col-span-2">{field('background', 'Project background', true)}</div>
              <div className="sm:col-span-2">{field('challenge', 'Challenge', true)}</div>
              <div className="sm:col-span-2">{field('objectives', 'Objectives', true)}</div>
              <div className="sm:col-span-2">{field('scope_of_work', 'Scope of work', true)}</div>
              <div className="sm:col-span-2">{field('methodology', 'Research methodology', true)}</div>
              <div className="sm:col-span-2">{field('deliverables', 'Deliverables', true)}</div>
              <div className="sm:col-span-2">{field('outcomes', 'Outcomes & recommendations', true)}</div>
            </>
          )}
          <label className="flex items-center gap-3 sm:col-span-2" data-testid="editor-published-label">
            <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-[#58A934]" data-testid="editor-published" />
            <span className="text-sm font-semibold text-brand-black">Published (visible on the public website)</span>
          </label>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={save} data-testid="editor-save" className="rounded-full bg-brand-green px-6 py-3 font-heading text-sm font-bold text-white hover:bg-brand-deepgreen">Save</button>
          <button onClick={() => setEditing(null)} data-testid="editor-cancel" className="rounded-full border border-brand-grey px-6 py-3 font-heading text-sm font-bold text-brand-black">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`admin-${kind}-tab`}>
      <button onClick={() => setEditing({ ...empty })} data-testid={`${kind}-new-button`} className="mb-5 flex items-center gap-2 rounded-full bg-brand-green px-5 py-2.5 font-heading text-sm font-bold text-white hover:bg-brand-deepgreen">
        <Plus size={15} /> New {isInsight ? 'Article' : 'Case Study'}
      </button>
      {items.length === 0 && <p className="py-16 text-center text-sm text-brand-body">Nothing here yet.</p>}
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item[idKey]} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-grey bg-white p-5" data-testid={`${kind}-row-${item[idKey]}`}>
            <div>
              <p className="font-heading text-sm font-bold text-brand-black">{item.title}</p>
              <p className="mt-1 text-xs text-brand-body">
                {item.category} · {item.published
                  ? <span className="font-semibold text-brand-deepgreen">Published</span>
                  : <span className="font-semibold text-amber-600">Draft</span>}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => togglePublish(item)} data-testid={`${kind}-toggle-${item[idKey]}`} className="rounded-full border border-brand-grey p-2 text-brand-body hover:border-brand-green hover:text-brand-deepgreen" aria-label={item.published ? 'Unpublish' : 'Publish'}>
                {item.published ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => setEditing({ ...empty, ...item })} data-testid={`${kind}-edit-${item[idKey]}`} className="rounded-full border border-brand-grey p-2 text-brand-body hover:border-brand-green hover:text-brand-deepgreen" aria-label="Edit">
                <Pencil size={14} />
              </button>
              <button onClick={() => remove(item[idKey])} data-testid={`${kind}-delete-${item[idKey]}`} className="rounded-full border border-brand-grey p-2 text-brand-body hover:border-red-300 hover:text-red-500" aria-label="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState('enquiries');

  useEffect(() => {
    if (location.state?.user) return;
    api.get('/auth/me')
      .then((r) => { setUser(r.data); setChecking(false); })
      .catch(() => navigate('/admin/login', { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post('/auth/logout');
    navigate('/admin/login', { replace: true });
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-brand-offwhite"><div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-green border-t-transparent" /></div>;
  }

  const TABS = [
    ['enquiries', 'Enquiries', MailOpen],
    ['insights', 'Insights Articles', Pencil],
    ['case-studies', 'Case Studies', Eye],
  ];

  return (
    <div className="min-h-screen bg-brand-offwhite" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard | INSIGHTS STRATEGY" description="Authorised access only." path="/admin" />
      <header className="sticky top-0 z-40 border-b border-brand-grey bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-header.webp" alt="INSIGHTS STRATEGY" className="h-auto w-[150px]" />
            <span className="hidden rounded-full bg-brand-deep px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-green sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-brand-body sm:block" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-brand-grey px-4 py-2 text-xs font-bold text-brand-black hover:border-brand-green">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label="Admin sections">
          {TABS.map(([id, label, Icon]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-all ${
                tab === id ? 'border-brand-green bg-brand-green text-white' : 'border-brand-grey bg-white text-brand-black/70 hover:border-brand-green/50'
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {tab === 'enquiries' && <EnquiriesTab />}
        {tab === 'insights' && <ContentTab kind="insights" />}
        {tab === 'case-studies' && <ContentTab kind="case-studies" />}
      </main>
    </div>
  );
}
